import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Divider, Empty, Row, Spin, Statistic, Table, Tag, Typography } from "antd";
import moment from "moment";
import OrganizerSelect from "../steps/OrganizerSelect";
import BookingMetrics from "./BookingMetrics";
import ListingMetricsDrawer from "./ListingMetricsDrawer";
import {
  getOrganizerMetrics,
  getOrganizerPackages,
  getOrganizerQuickTrips,
} from "../../../services/packages-service";

const { Title, Text } = Typography;
const PAGE_SIZE = 10;

const money = (price) =>
  price ? `${price.currency || "NGN"} ${Number(price.amount || 0).toLocaleString()}` : "—";

const listingStatus = (record) => {
  if (record.deleted_at) return <Tag color="red">Deleted</Tag>;
  if (record.end_date && moment(record.end_date).isBefore(moment())) return <Tag>Expired</Tag>;
  return <Tag color="blue">Active</Tag>;
};

// { total, active, expired, deleted } for packages or quick trips
const ListingCounts = ({ title, counts }) => (
  <Card size="small" title={title}>
    <Row gutter={8}>
      <Col span={6}><Statistic title="Total" value={counts.total} /></Col>
      <Col span={6}><Statistic title="Active" value={counts.active} /></Col>
      <Col span={6}><Statistic title="Expired" value={counts.expired} /></Col>
      <Col span={6}><Statistic title="Deleted" value={counts.deleted} /></Col>
    </Row>
  </Card>
);

// One organizer's packages or quick trips, paginated server-side.
const OrganizerListings = ({ organizerId, type, onShowMetrics }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (page = 0) => {
    setLoading(true);
    setError(null);
    try {
      const fetchListings = type === "QUICK_TRIP" ? getOrganizerQuickTrips : getOrganizerPackages;
      setResult(await fetchListings(organizerId, { page, pageSize: PAGE_SIZE }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizerId, type]);

  if (error) return <Alert type="error" showIcon message="Couldn't load listings" description={error} />;

  return (
    <Table
      rowKey="id"
      size="small"
      loading={loading}
      dataSource={result?.data || []}
      locale={{ emptyText: `No ${type === "QUICK_TRIP" ? "quick trips" : "packages"} assigned` }}
      pagination={{
        current: (result?.page ?? 0) + 1,
        pageSize: PAGE_SIZE,
        total: result?.total || 0,
        onChange: (page) => load(page - 1),
      }}
      columns={[
        { title: "Title", dataIndex: "title", key: "title" },
        { title: "Destination", key: "destination", render: (_, r) => r.location?.name || "—" },
        { title: "Price", key: "price", render: (_, r) => money(r.price) },
        { title: "Status", key: "status", render: (_, r) => listingStatus(r) },
        {
          title: "Ends",
          key: "end_date",
          render: (_, r) => (r.end_date ? moment(r.end_date).format("YYYY-MM-DD") : "—"),
        },
        {
          title: "Actions",
          key: "actions",
          render: (_, r) => (
            <Button size="small" onClick={() => onShowMetrics(r, type)}>
              Metrics
            </Button>
          ),
        },
      ]}
    />
  );
};

/**
 * "Organizer Metrics" tab: pick a partner, then see their totals across all
 * assigned packages/quick trips, and drill into any single listing.
 */
const OrganizerMetricsPanel = () => {
  const [organizerId, setOrganizerId] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drawer, setDrawer] = useState({ listing: null, type: null });

  useEffect(() => {
    if (!organizerId) {
      setMetrics(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getOrganizerMetrics(organizerId)
      .then((data) => !cancelled && setMetrics(data))
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [organizerId]);

  const showListingMetrics = (listing, type) => setDrawer({ listing, type });

  return (
    <div>
      <div style={{ maxWidth: 480, marginBottom: 24 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>Organizer</div>
        <OrganizerSelect
          value={organizerId}
          onChange={setOrganizerId}
          placeholder="Select a partner to view their metrics"
        />
      </div>

      {!organizerId && (
        <Empty description="Pick a partner above to see their packages, quick trips and bookings" />
      )}

      {organizerId && loading && <Spin />}
      {organizerId && error && (
        <Alert type="error" showIcon message="Couldn't load organizer metrics" description={error} />
      )}

      {organizerId && metrics && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <ListingCounts title="Packages" counts={metrics.packages} />
            </Col>
            <Col xs={24} lg={12}>
              <ListingCounts title="Quick Trips" counts={metrics.quickTrips} />
            </Col>
          </Row>

          <Title level={5} style={{ marginTop: 24 }}>Bookings across all listings</Title>
          <BookingMetrics metrics={metrics} />

          <Divider />
          <Title level={5}>Packages</Title>
          <OrganizerListings organizerId={organizerId} type="PACKAGE" onShowMetrics={showListingMetrics} />

          <Title level={5} style={{ marginTop: 24 }}>Quick Trips</Title>
          <OrganizerListings organizerId={organizerId} type="QUICK_TRIP" onShowMetrics={showListingMetrics} />

          <Text type="secondary" style={{ fontSize: 12 }}>
            Bookings follow a listing&apos;s current organizer — reassigning a listing moves its
            booking history with it.
          </Text>
        </>
      )}

      <ListingMetricsDrawer
        listing={drawer.listing}
        type={drawer.type}
        onClose={() => setDrawer({ listing: null, type: null })}
      />
    </div>
  );
};

export default OrganizerMetricsPanel;
